/**
 * Created: 2026-08-05
 * Initial commit: Add admin dashboard with DAU chart
 *
 * Admin dashboard page showing daily active users (DAU) for the selected
 * date range. Moved from the site's DashboardPage — DAU is a global metric
 * (not scoped to any site), so it belongs on the admin app's dashboard
 * rather than a per-site one.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Alert, Box, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { addDays, endOfDay, format, startOfDay } from 'date-fns'
import { useState } from 'react'
import DashboardRangePicker, { defaultRange } from '../components/DashboardRangePicker'
import { DailyDauDocument } from '../gql/graphql'

gql`
  query DailyDau($startDate: DateTime!, $endDate: DateTime!) {
    dailyDau(startDate: $startDate, endDate: $endDate) {
      date
      count
    }
  }
`

export default function AdminDashboardPage() {
  const [range, setRange] = useState<DateRange<Date>>(defaultRange)

  const [startDate, endDate] = range
  const queryStart = startDate ? startOfDay(startDate).toISOString() : null
  const queryEnd = endDate ? endOfDay(endDate).toISOString() : null

  const {
    data: dauData,
    loading: dauLoading,
    error: dauError,
  } = useQuery(DailyDauDocument, {
    variables: { startDate: queryStart ?? '', endDate: queryEnd ?? '' },
    skip: !queryStart || !queryEnd,
    fetchPolicy: 'network-only',
  })

  // Build date list for x-axis
  const allDates: string[] = []
  if (
    startDate &&
    endDate &&
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime())
  ) {
    let current = startOfDay(startDate)
    const end = startOfDay(endDate)
    while (current <= end) {
      allDates.push(format(current, 'yyyy-MM-dd'))
      current = addDays(current, 1)
    }
  }

  // DAU chart data — padded to allDates so its x-axis is the full selected
  // range instead of only showing whatever dates the backend happened to
  // return.
  const dauPoints = dauData?.dailyDau ?? []
  const dauMap = new Map(dauPoints.map((p) => [p.date, p.count]))
  const dauCounts = allDates.map((d) => dauMap.get(d) ?? 0)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">数据看板</Typography>
        <Box sx={{ flex: 1 }} />
        <DashboardRangePicker range={range} onRangeChange={setRange} />
      </Box>

      {dauError && <Alert severity="error">加载数据失败: {dauError.message}</Alert>}

      {/* DAU chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          日活跃用户 (DAU)
        </Typography>
        <LineChart
          loading={dauLoading}
          xAxis={[
            {
              scaleType: 'band',
              data: allDates,
              tickLabelStyle: { fontSize: 11 },
            },
          ]}
          yAxis={[{ min: 0, valueFormatter: (v: number) => `${v}人` }]}
          series={[
            {
              data: dauCounts,
              label: '活跃用户数',
              color: '#ed6c02',
              showMark: true,
            },
          ]}
          height={300}
          margin={{ left: 60, right: 20, top: 20, bottom: 60 }}
        />
      </Box>
    </Box>
  )
}
